import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import Login from "./pages/Login/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import KYCPage from "./pages/KYC/KYCPage";
import UserPage from "./pages/USER/UserPage";
import KYCRequestForm from "./pages/KYC/KYCForm";
import UserForm from "./pages/USER/UserForm";
import BusinessPage from "./pages/Businesses/BusinessPage";
import BusinessDetailForm from "./pages/Businesses/BusinessForm";
import EditUser from "./pages/USER/EditUser";
import PayoutReview from "./pages/ManualReview/PayoutReview";
import DepositReview from "./pages/ManualReview/DepositReview";
import BusinessDepositReview from "./pages/ManualReview/BusinessDepositReview";
import PendingPayouts from "./pages/PendingPayouts/PendingPayouts";
import FailedPayouts from "./pages/FailedPayouts/FailedPayouts";
import MerchantPayouts from "./pages/MerchantPayouts/MerchantPayouts";
import ReserveTrans from "./pages/Reserve/ReserveTrans";
import Webhooks from "./pages/webhooks/Webhooks";
import GiftCardBuyOrders from "./pages/GiftCardOrders/GiftCardBuyOrders";
import GiftCardSellOrders from "./pages/GiftCardOrders/GiftCardSellOrders";
import CurrencySettings from "./pages/currency-settings/CurrencySettings";
import CountrySettings from "./pages/country-settings/CountrySettings";
import PayoutPartnerSettings from "./pages/payout-partner-settings/PayoutPartnerSettings";
import ConsumerPayoutSettings from "./pages/consumer-payout-settings/ConsumerPayoutSettings";
import ConsumerNotificationSettings from "./pages/consumer-notification-settings/ConsumerNotificationSettings";
import Announcements from "./pages/announcements/Announcements";
import CouponCampaigns from "./pages/CouponCampaigns/CouponCampaigns";
import CampaignDetailTabs from "./pages/CouponCampaigns/components/CampaignDetailTabs";
import SendProductUpdate from "./pages/SendProductUpdate/SendProductUpdate";
import PartnerBalancesPage from "./pages/Treasury/PartnerBalancesPage";
import DailyPositionPage from "./pages/Treasury/DailyPositionPage";
import ConsumerMerchantPositionPage from "./pages/Treasury/ConsumerMerchantPositionPage";
import ConversionLogPage from "./pages/Treasury/ConversionLogPage";
import PartnerConversionPage from "./pages/Treasury/PartnerConversionPage";
import OrphanedPayoutsPage from "./pages/Treasury/OrphanedPayoutsPage";
import FeeMarginPage from "./pages/Treasury/FeeMarginPage";
import CryptoNetworks from "./pages/crypto-networks/CryptoNetworks";

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.accessToken);
  const role = useSelector((state) => state.auth.role);

  if (!isAuthenticated && role !== "admin") {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.accessToken);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Route */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kyc-requests"
          element={
            <ProtectedRoute>
              <KYCPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business"
          element={
            <ProtectedRoute>
              <BusinessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/:id"
          element={
            <ProtectedRoute>
              <BusinessDetailForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payout-review"
          element={
            <ProtectedRoute>
              <PayoutReview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deposit-review"
          element={
            <ProtectedRoute>
              <DepositReview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/business-deposit-review"
          element={
            <ProtectedRoute>
              <BusinessDepositReview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treasury/partner-balances"
          element={
            <ProtectedRoute>
              <PartnerBalancesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treasury/daily-position"
          element={
            <ProtectedRoute>
              <DailyPositionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treasury/consumer-merchant-position"
          element={
            <ProtectedRoute>
              <ConsumerMerchantPositionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treasury/conversion-log"
          element={
            <ProtectedRoute>
              <ConversionLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treasury/partner-conversion"
          element={
            <ProtectedRoute>
              <PartnerConversionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/treasury/orphaned-payouts"
          element={
            <ProtectedRoute>
              <OrphanedPayoutsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchant-payouts"
          element={
            <ProtectedRoute>
              <MerchantPayouts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/failed-payouts"
          element={
            <ProtectedRoute>
              <FailedPayouts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pending-payouts"
          element={
            <ProtectedRoute>
              <PendingPayouts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reserve-transactions"
          element={
            <ProtectedRoute>
              <ReserveTrans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gift-card-buy-orders"
          element={
            <ProtectedRoute>
              <GiftCardBuyOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gift-card-sell-orders"
          element={
            <ProtectedRoute>
              <GiftCardSellOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kyc-requests/:id"
          element={
            <ProtectedRoute>
              <KYCRequestForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:uuid"
          element={
            <ProtectedRoute>
              <UserForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:uuid/edit"
          element={
            <ProtectedRoute>
              <EditUser />
            </ProtectedRoute>
          }
        />

        <Route
          path="/webhooks"
          element={
            <ProtectedRoute>
              <Webhooks />
            </ProtectedRoute>
          }
        />

        <Route
          path="/currency-settings"
          element={
            <ProtectedRoute>
              <CurrencySettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/country-settings"
          element={
            <ProtectedRoute>
              <CountrySettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/crypto-networks"
          element={
            <ProtectedRoute>
              <CryptoNetworks />
            </ProtectedRoute>
          }
        />

        <Route
          path="/treasury/fee-margin"
          element={
            <ProtectedRoute>
              <FeeMarginPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payout-partner-settings"
          element={
            <ProtectedRoute>
              <PayoutPartnerSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/announcements"
          element={
            <ProtectedRoute>
              <Announcements />
            </ProtectedRoute>
          }
        />

        <Route
          path="/consumer-payout-settings"
          element={
            <ProtectedRoute>
              <ConsumerPayoutSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/consumer-notification-settings"
          element={
            <ProtectedRoute>
              <ConsumerNotificationSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/coupon-campaigns"
          element={
            <ProtectedRoute>
              <CouponCampaigns />
            </ProtectedRoute>
          }
        />

        <Route
          path="/coupon-campaigns/:uuid"
          element={
            <ProtectedRoute>
              <CampaignDetailTabs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/send-product-update"
          element={
            <ProtectedRoute>
              <SendProductUpdate />
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
